import { Backdrop, Box, Button, CardActionArea, Chip, CircularProgress, Container, Divider, Grid, IconButton, Skeleton, Tooltip, Typography, alpha } from "@mui/material";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useEstabelecimentos } from "../../hooks/useEstabelecimentos";
import { useEffect, useState } from "react";
import { IAcessoUsuario } from "../../../usuarios/interfaces/IAcessoUsuario";
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { PaperEstabelecimento, LogoContainer, TituloPagina } from "./styles";
import { Add, ArrowForward, LockOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apresentarSituacao } from "../../utils/situacaoAcesso";

export const ListarEstabelecimentos = () => {
    const [acessos, setAcessos] = useState<IAcessoUsuario[]>();
    const [acessando, setAcessando] = useState(false);
    const { alterarAcesso, entrarEstabelecimento, organizacoes } = useAutenticacaoContext();
    const { buscarEstabelecimentosUsuario, loading } = useEstabelecimentos();
    const navigate = useNavigate();

    const handleAcessar = async (acesso: IAcessoUsuario) => {
        setAcessando(true);
        try {
            // Acesso direto possui uuid; estabelecimento de organização (acesso dinâmico) não possui
            // — nesse caso entramos pelo estabelecimentoUuid validando a participação na organização.
            if (acesso.uuid) {
                await alterarAcesso(acesso.uuid);
            } else {
                await entrarEstabelecimento(acesso.estabelecimentoUuid);
            }
        } finally {
            // Estabelecimento bloqueado responde 403 e não navega: sem isso o
            // overlay de carregamento ficaria girando para sempre.
            setAcessando(false);
        }
    }

    const handleListarAcessosEstabelecimentos = async () => {
        const acessosEstabelecimentos = await buscarEstabelecimentosUsuario();
        setAcessos(acessosEstabelecimentos);
    }

    useEffect(() => {
        handleListarAcessosEstabelecimentos();
        alterarAcesso();
    }, [])

    useEffect(() => {
        if (!loading && acessos !== undefined && acessos.length === 0) {
            // Membros de organização criam pela tela própria (com seleção de plano).
            navigate(organizacoes?.length ? "/organizacoes/estabelecimentos/criar" : "/estabelecimentos/criar");
        }
    }, [loading, acessos])

    const getLogo = (acesso: IAcessoUsuario) => {
        return acesso.tema?.imagens?.find(img => img.tipo === "Logo")?.url;
    }

    const formatEndereco = (acesso: IAcessoUsuario) => {
        const end = acesso.estabelecimento?.endereco;
        if (!end?.rua) return null;
        const parts = [end.rua, end.numero].filter(Boolean).join(", ");
        return end.bairro ? `${parts} - ${end.bairro}` : parts;
    }

    return (
        <>
            <Backdrop open={acessando} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Container>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <TituloPagina item xs={12}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">Seus estabelecimentos</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gerencie todos os seus negócios em um só lugar.
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<Add />} onClick={() => navigate(organizacoes?.length ? "/organizacoes/estabelecimentos/criar" : "/estabelecimentos/criar")}>NOVO ESTABELECIMENTO</Button>
                </TituloPagina>
                <Grid item xs={12} sx={{ mt: 4 }}>
                    {loading ? (
                        <Grid container spacing={4}>
                            {Array.from({ length: 6 }, (_, index) => (
                                <Grid item xs={12} md={4} sm={6} key={index}>
                                    <Skeleton variant="rounded" height={200} sx={{ borderRadius: "32px" }} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        acessos?.length ? (
                            <Grid container spacing={4}>
                                {acessos.map((acesso) => {
                                    const logoUrl = getLogo(acesso);
                                    const endereco = formatEndereco(acesso);
                                    const apresentacao = apresentarSituacao(acesso.situacao);
                                    const bloqueado = !!acesso.situacao?.bloqueado;

                                    return (
                                        <Grid key={acesso.uuid ?? acesso.estabelecimentoUuid} item md={4} xs={12} sm={6}>
                                            <PaperEstabelecimento
                                                bloqueado={bloqueado}
                                                onClick={bloqueado ? undefined : () => handleAcessar(acesso)}
                                            >
                                                <CardActionArea
                                                    disableRipple={bloqueado}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        padding: "1.5rem",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "flex-start",
                                                        justifyContent: "space-between",
                                                        gap: 1.5,
                                                        cursor: "inherit",
                                                        ...(bloqueado && {
                                                            "& .MuiCardActionArea-focusHighlight": { opacity: 0 }
                                                        })
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 2,
                                                        width: "100%",
                                                        // Só a identificação desbota; o chip continua legível.
                                                        opacity: bloqueado ? 0.55 : 1
                                                    }}>
                                                        <LogoContainer>
                                                            {logoUrl ? (
                                                                <img
                                                                    src={logoUrl}
                                                                    alt={acesso.estabelecimento?.nomeFantasia}
                                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                                />
                                                            ) : (
                                                                <StorefrontIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                                                            )}
                                                        </LogoContainer>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ textTransform: "uppercase" }}>
                                                                {acesso.estabelecimento?.nomeFantasia}
                                                            </Typography>
                                                            {endereco && (
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                                                    <LocationOnIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                                        {endereco}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    <Divider sx={{ width: "100%", mt: 1 }} />

                                                    <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                            <Tooltip arrow title={
                                                                apresentacao.motivos.length ? (
                                                                    <Box
                                                                        component="ul"
                                                                        sx={{
                                                                            m: 0,
                                                                            pl: apresentacao.motivos.length > 1 ? 2 : 0,
                                                                            listStyle: apresentacao.motivos.length > 1 ? "disc" : "none"
                                                                        }}
                                                                    >
                                                                        {apresentacao.motivos.map((motivo) => (
                                                                            <li key={motivo}>{motivo}</li>
                                                                        ))}
                                                                    </Box>
                                                                ) : ""
                                                            }>
                                                                <Chip
                                                                    label={apresentacao.rotulo}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: alpha(apresentacao.hex, 0.12),
                                                                        color: apresentacao.hex,
                                                                        fontWeight: 600,
                                                                        cursor: apresentacao.motivos.length ? "help" : "inherit"
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            {acesso.perfil?.nome && (
                                                                <Chip
                                                                    label={acesso.perfil.nome}
                                                                    color="primary"
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                        {bloqueado ? (
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled", px: 1 }}>
                                                                <LockOutlined sx={{ fontSize: 16 }} />
                                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>Sem acesso</Typography>
                                                            </Box>
                                                        ) : (
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    "&:hover .acessar-text": {
                                                                        textDecoration: "underline"
                                                                    }
                                                                }}
                                                            >
                                                                <Typography className="acessar-text" variant="caption" sx={{ mr: 0.5, fontWeight: 600 }}>Acessar</Typography>
                                                                <ArrowForward sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </CardActionArea>
                                            </PaperEstabelecimento>
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        ) : (
                            <Box>Nenhum estabelecimento encontrado</Box>
                        )
                    )}
                </Grid>
            </Grid>
        </Container>
        </>
    );
}
